import { Autocomplete, Box, createFilterOptions, TextField } from '@mui/material';

import { filterFieldSx } from './filterFieldSx';
import TrackedPlayerRow from './TrackedPlayerRow';

const ALL = 'all';

export type PlayerFilterOption = {
  value: string;
  /** "#97 Connor McDavid" — what the input shows once one is picked. */
  label: string;
  playerNumber: string;
  playerName: string;
  playerPosition: string;
  headshotUrl: string | null;
  teamAbbreviation: string;
  teamLogoUrl: string | null;
  teamColor: string;
};

type Props = {
  value: string;
  onChange: (value: string) => void;
  options: PlayerFilterOption[];
};

// Position and team are only rendered on the row, never in the label, so they
// have to be stringified in or they are unsearchable.
const filterPlayers = createFilterOptions<PlayerFilterOption>({
  stringify: (option) => `${option.label} ${option.playerPosition} ${option.teamAbbreviation}`,
});

/**
 * Player picker. Each row is the headshot and team crest paired, then jersey,
 * name and position — the game page's `TrackedItem` layout, rendered through
 * the local `TrackedPlayerRow` so the option keeps its own hover and selected
 * backgrounds instead of an opaque row covering them.
 *
 * Clearing the input is what selects "all players"; there is no explicit row for
 * it, which is how an Autocomplete is normally driven.
 */
const PlayerSelect = ({ value, onChange, options }: Props) => {
  const selected = options.find((option) => option.value === value) ?? null;

  return (
    <Autocomplete
      options={options}
      value={selected}
      onChange={(_event, next) => onChange(next?.value ?? ALL)}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, other) => option.value === other.value}
      filterOptions={filterPlayers}
      groupBy={(option) => option.teamAbbreviation || 'Unknown team'}
      selectOnFocus
      handleHomeEndKeys
      sx={{ width: 240 }}
      renderOption={(props, option) => {
        const { key, ...rest } = props;

        return (
          <Box component="li" {...rest} key={key} sx={{ cursor: 'pointer', padding: '4px 8px' }}>
            <TrackedPlayerRow
              playerNumber={option.playerNumber}
              playerName={option.playerName}
              playerPosition={option.playerPosition}
              headshotUrl={option.headshotUrl}
              teamAbbreviation={option.teamAbbreviation}
              teamLogoUrl={option.teamLogoUrl}
              teamColor={option.teamColor}
              selected={option.value === value}
            />
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="All players"
          size="small"
          sx={filterFieldSx}
        />
      )}
    />
  );
};

export default PlayerSelect;
